import type { TFunction } from "i18next";
import {
  ArchiveIcon,
  ArchiveRestoreIcon,
  ArchiveXIcon,
  CheckIcon,
  CircleDotDashedIcon,
  ClockIcon,
  CornerDownLeftIcon,
  HourglassIcon,
  PauseIcon,
  ScanSearchIcon,
} from "lucide-react";

import { SupportTicketSubStatus } from "~/graphql/graphql.gen";

export const NeedsFirstResponseIcon = CircleDotDashedIcon;
export const NeedsNextResponseIcon = CornerDownLeftIcon;
export const InvestigatingIcon = ScanSearchIcon;
export const WaitingForContactIcon = ClockIcon;
export const WaitingForSupplierIcon = HourglassIcon;
export const PausedForLaterIcon = PauseIcon;
export const ResolvedIcon = CheckIcon;
export const ArchivedIcon = ArchiveIcon;
export const IgnoredIcon = ArchiveXIcon;
export const OnlyInformationIcon = ArchiveRestoreIcon;

export const getSubStatuses = (t: TFunction) => [
  {
    value: SupportTicketSubStatus.NeedsFirstResponse,
    label: t("ticket.sub_status.needs_first_response"),
    color: "var(--color-todo)",
    icon: NeedsFirstResponseIcon,
  },
  {
    value: SupportTicketSubStatus.NeedsNextResponse,
    label: t("ticket.sub_status.needs_next_response"),
    color: "var(--color-todo)",
    icon: NeedsNextResponseIcon,
  },
  {
    value: SupportTicketSubStatus.Investigating,
    label: t("ticket.sub_status.investigating"),
    color: "var(--color-todo)",
    icon: InvestigatingIcon,
  },
  {
    value: SupportTicketSubStatus.WaitingContact,
    label: t("ticket.sub_status.waiting_for_contact"),
    color: "var(--color-snoozed)",
    icon: WaitingForContactIcon,
  },
  {
    value: SupportTicketSubStatus.WaitingSupplier,
    label: t("ticket.sub_status.waiting_for_supplier"),
    color: "var(--color-snoozed)",
    icon: WaitingForSupplierIcon,
  },
  {
    value: SupportTicketSubStatus.PausedForLater,
    label: t("ticket.sub_status.paused_for_later"),
    color: "var(--color-snoozed)",
    icon: PausedForLaterIcon,
  },
  {
    value: SupportTicketSubStatus.Resolved,
    label: t("ticket.sub_status.resolved"),
    color: "var(--color-done)",
    icon: ResolvedIcon,
  },
  {
    value: SupportTicketSubStatus.OnlyInformation,
    label: t("ticket.sub_status.only_information"),
    color: "var(--color-done)",
    icon: OnlyInformationIcon,
  },
  {
    value: SupportTicketSubStatus.Ignored,
    label: t("ticket.sub_status.ignored"),
    color: "var(--color-done)",
    icon: IgnoredIcon,
    disabled: true,
  },
  {
    value: SupportTicketSubStatus.Archived,
    label: t("ticket.sub_status.archived"),
    color: "var(--color-done)",
    icon: ArchivedIcon,
    disabled: true,
  },
];

const NEEDS_FIRST_RESPONSE = "todo/needs-first-response";
const NEEDS_NEXT_RESPONSE = "todo/needs-next-response";
const INVESTIGATING = "todo/investigating";
const WAITING_FOR_CONTACT = "snoozed/waiting-for-contact";
const WAITING_FOR_SUPPLIER = "snoozed/waiting-for-supplier";
const PAUSED_FOR_LATER = "snoozed/paused-for-later";
const RESOLVED = "done/resolved";
const ARCHIVED = "done/archived";
const IGNORED = "done/ignored";
const ONLY_INFORMATION = "done/only-information";

export const subStatusQueryParamMapping = [
  {
    filter: NEEDS_FIRST_RESPONSE,
    status: SupportTicketSubStatus.NeedsFirstResponse,
  },
  {
    filter: NEEDS_NEXT_RESPONSE,
    status: SupportTicketSubStatus.NeedsNextResponse,
  },
  {
    filter: INVESTIGATING,
    status: SupportTicketSubStatus.Investigating,
  },
  {
    filter: WAITING_FOR_CONTACT,
    status: SupportTicketSubStatus.WaitingContact,
  },
  {
    filter: WAITING_FOR_SUPPLIER,
    status: SupportTicketSubStatus.WaitingSupplier,
  },
  {
    filter: PAUSED_FOR_LATER,
    status: SupportTicketSubStatus.PausedForLater,
  },
  { filter: RESOLVED, status: SupportTicketSubStatus.Resolved },
  { filter: ARCHIVED, status: SupportTicketSubStatus.Archived },
  { filter: IGNORED, status: SupportTicketSubStatus.Ignored },
  { filter: ONLY_INFORMATION, status: SupportTicketSubStatus.OnlyInformation },
] as const;

export const subStatusQueryParamValues = subStatusQueryParamMapping.map(
  (item) => item.filter,
) as unknown as [
  typeof NEEDS_FIRST_RESPONSE,
  typeof NEEDS_NEXT_RESPONSE,
  typeof INVESTIGATING,
  typeof WAITING_FOR_CONTACT,
  typeof WAITING_FOR_SUPPLIER,
  typeof PAUSED_FOR_LATER,
  typeof RESOLVED,
  typeof ARCHIVED,
  typeof IGNORED,
  typeof ONLY_INFORMATION,
];

export const sortSubStatus = (
  a: SupportTicketSubStatus,
  b: SupportTicketSubStatus,
) => {
  const map = new Map<string, number>();
  map.set(SupportTicketSubStatus.NeedsFirstResponse, 0);
  map.set(SupportTicketSubStatus.NeedsNextResponse, 1);
  map.set(SupportTicketSubStatus.Investigating, 2);
  map.set(SupportTicketSubStatus.WaitingContact, 3);
  map.set(SupportTicketSubStatus.WaitingSupplier, 4);
  map.set(SupportTicketSubStatus.PausedForLater, 5);
  map.set(SupportTicketSubStatus.Resolved, 6);
  map.set(SupportTicketSubStatus.OnlyInformation, 7);
  map.set(SupportTicketSubStatus.Archived, 8);

  if (
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    map.get(a)! <
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    map.get(b)!
  ) {
    return -1;
  }
  if (
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    map.get(a)! >
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    map.get(b)!
  ) {
    return 1;
  }
  return 0;
};

export const mapFilterToSubStatus = (filter: string) => {
  return subStatusQueryParamMapping.find((item) => item.filter === filter)
    ?.status;
};

export const mapSubStatusToFilter = (statusId: SupportTicketSubStatus) => {
  return subStatusQueryParamMapping.find((item) => item.status === statusId)
    ?.filter;
};
